<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { MetricType } from './utility';
import ProgramFilterDropdown from '@Shared/components/ProgramFilterDropdown.vue';
import SkeletonDriverGauges from './skeletons/DriverGauges.vue';
import EmptyStateIconUrl from '@Shared/components/visuals/visualAssets/EmptyState_Icon.svg?url';
import { useFrontlineFiltersStore } from '@Portal/stores/frontlineFilters';

const props = defineProps<{
  title: string;
  isFrontline: boolean;
  measureResults: Record<string, MeasureResult[]>;
  metricType: MetricType;
}>();

const frontlineStore = useFrontlineFiltersStore();
const selectedProgram = ref<string>('');
const getMeasureResultsForProgram = computed(() => {
  return selectedProgram.value ? props.measureResults[selectedProgram.value] : [];
});

onMounted(() => {
  if (
    frontlineStore.selectedProgramFilter &&
    props.measureResults[frontlineStore.selectedProgramFilter] !== undefined
  ) {
    selectedProgram.value = frontlineStore.selectedProgramFilter;
  } else {
    selectedProgram.value = Object.keys(props.measureResults)[0];
    frontlineStore.selectProgramFilter(Object.keys(props.measureResults)[0]);
  }
});
</script>

<template>
  <div id="program-side-bar" class="xl:flex flex-col block xs:hidden shrink-0 w-[140px] bg-[#F4F6Fb] sticky top-[0]">
    <button
      class="text-left pl-2 w-100% color-[#4F4F4F] py-2.5 border-none cursor-pointer break-words font-[Roboto]"
      :class="[selectedProgram === key ? 'font-semibold bg-[#FFFFFF]' : 'font-normal bg-[#F4F6FB]']"
      v-for="(result, key) in measureResults"
      :key="key"
      @click="
        selectedProgram = key;
        frontlineStore.selectProgramFilter(key);
      "
    >
      {{ key }}
    </button>
  </div>
  <div class="flex flex-row w-full md:pr-6 bg-[#FFFFFF]">
    <div class="flex flex-wrap h-full w-full flex-col content-center">
      <div class="xl:flex flex-col xs:hidden shrink-0 w-[140px] bg-[#F4F6Fb]"></div>
      <div class="flex flex-wrap xl:w-4/5 xs:flex-row xl:flex-col xs:w-full">
        <div class="xl:hidden w-full">
          <ProgramFilterDropdown class="shrink-0 w-[95%]" :measureResults="measureResults" v-model="selectedProgram" />
        </div>
        <!-- Only render if any of the measure results contain 'currentValue' so that 
             the gauges draw correctly, otherwise show empty state -->
        <div v-if="getMeasureResultsForProgram.some((m) => m.currentValue !== undefined)">
          <!-- Strongest, Losing Strength, and Weakest DriverGauge components -->
          <DriverGauges
            title=""
            :isFrontline="true"
            :inPopup="true"
            :measureResults="getMeasureResultsForProgram"
            :metricType="metricType"
          >
          </DriverGauges>
        </div>
        <div v-else>
          <div class="relative flex flex-col w-full md:pr-6 bg-[#FFFFFF] items-center">
            <div class="flex flex-row mt-10">
              <SkeletonDriverGauges :hasNoData="true" :isFrontline="isFrontline" :inPopup="true" />
            </div>
            <hr class="xs:hidden xl:block h-[1px] w-full mt-1 b-0 mb-4 text-[#E0E0E0] bg-[#E0E0E0]" />
            <div class="xs:hidden xl:flex flex-row">
              <SkeletonDriverGauges :hasNoData="true" :isFrontline="isFrontline" :inPopup="true" />
            </div>
            <div class="xs:hidden xl:flex flex-row">
              <SkeletonDriverGauges :hasNoData="true" :isFrontline="isFrontline" :inPopup="true" />
            </div>
            <div
              class="xs:top-15% xs:text-4 xs:line-height-1.5rem absolute xl:top-40% text-center w-full text-[#828282] xl:text-sm xl:text-3 -3rem font-family[Roboto] xl:line-height-1rem"
            >
              <img :src="EmptyStateIconUrl" alt="" />
              <div class="xs:max-w-[250px] xl:max-w-[300px] m-auto">
                <span>
                  Nothing to see here. <br />
                  You can try changing the <strong>filter</strong> or checking the
                  <strong>other programs.</strong>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
